import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  useGetConsoleWebsiteByIdQuery,
  useUpdateConsoleWebsiteMutation,
  selectCurrentConsoleWebsiteId,
} from "../../app/api/console/consoleWebsitesApiSlice";
import {
  useGetWebsiteByIdQuery as useGetPlatformWebsiteByIdQuery,
  useUpdateWebsiteMutation,
  useCreateWebsiteMutation,
} from "../../app/api/platform/platformWebsitesApiSlice";
import { PLAN_OPTIONS } from "../../constants/customers";

// Mirrors Region enum in ZenytCommon/src/zenytcommon/models/regions.py
const REGION_OPTIONS = [
  "GLOBAL", "EU",
  "US", "CA", "MX", "BR", "AR", "PE", "GT", "PR", "GL",
  "UK", "SCOTLAND", "FR", "DE", "IT", "ES", "BE", "NL", "PT", "PL",
  "CH", "AT", "SE", "NO", "DK", "FI", "GR", "RO", "HU", "SK", "SI", "LU", "IS", "IE",
  "RU", "TR", "UA", "MD", "MC",
  "MA", "DZ", "TN", "LB", "IL", "SA", "AE", "IR", "KZ", "ZA",
  "IN", "ID", "TH", "SG", "HK", "CN", "JP", "KR",
  "AU", "NZ",
];

const inputClass = "w-full px-3 py-2.5 text-body-sm bg-white border border-border-subtle rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 transition-all";
const readonlyClass = "w-full px-3 py-2.5 text-body-sm bg-surface-neutral border border-border-subtle rounded-xl text-text-secondary select-all cursor-default";
const labelClass = "block text-body-xs font-medium text-text-secondary mb-1.5";

function Toggle({ enabled, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
        enabled ? "bg-accent-purple" : "bg-surface-neutral-subtle"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${enabled ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

function SettingRow({ label, description, children }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 min-w-0 mr-4">
        <p className="text-body-sm font-medium text-text-primary">{label}</p>
        {description && <p className="text-caption text-text-tertiary mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function RegionUrlMappingEditor({ value, onChange, disabled }) {
  const mapping = value || {};
  const entries = Object.entries(mapping);
  const usedRegions = new Set(Object.keys(mapping));

  const handleAddRegion = () => {
    const next = REGION_OPTIONS.find((r) => !usedRegions.has(r));
    if (!next) return;
    onChange({ ...mapping, [next]: [""] });
  };

  const handleRemoveRegion = (region) => {
    const next = { ...mapping };
    delete next[region];
    onChange(next);
  };

  const handleRenameRegion = (oldRegion, newRegion) => {
    if (oldRegion === newRegion || mapping[newRegion]) return;
    // Rebuild preserving insertion order
    const next = {};
    for (const [k, v] of Object.entries(mapping)) {
      next[k === oldRegion ? newRegion : k] = v;
    }
    onChange(next);
  };

  const handleUpdateUrl = (region, idx, url) => {
    const urls = [...(mapping[region] || [])];
    urls[idx] = url;
    onChange({ ...mapping, [region]: urls });
  };

  const handleAddUrl = (region) => {
    const urls = [...(mapping[region] || []), ""];
    onChange({ ...mapping, [region]: urls });
  };

  const handleRemoveUrl = (region, idx) => {
    const urls = [...(mapping[region] || [])];
    urls.splice(idx, 1);
    onChange({ ...mapping, [region]: urls.length > 0 ? urls : [""] });
  };

  return (
    <div className="py-3">
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-body-sm font-medium text-text-primary">Region URL Mapping</p>
          <p className="text-caption text-text-tertiary">URLs per region for multi-country sites</p>
        </div>
        <button
          type="button"
          onClick={handleAddRegion}
          disabled={disabled || entries.length >= REGION_OPTIONS.length}
          className="px-2.5 py-1 text-caption font-medium text-accent-purple hover:bg-accent-purple/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
          + Add Region
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="bg-surface-neutral rounded-xl p-3 text-center">
          <p className="text-caption text-text-tertiary">No regions configured. Default uses website URL.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(([region, urls]) => (
            <div key={region} className="bg-surface-neutral rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={region}
                  onChange={(e) => handleRenameRegion(region, e.target.value)}
                  disabled={disabled}
                  className="px-2 py-1.5 text-body-xs font-medium bg-white border border-border-subtle rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-purple/20 min-w-[110px]">
                  {REGION_OPTIONS.map((r) => (
                    <option key={r} value={r} disabled={r !== region && usedRegions.has(r)}>
                      {r}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => handleRemoveRegion(region)}
                  disabled={disabled}
                  className="ml-auto px-2 py-1 text-caption font-medium text-severity-high hover:bg-severity-high/5 rounded-lg transition-colors">
                  Remove region
                </button>
              </div>
              <div className="space-y-1.5">
                {(urls || []).map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => handleUpdateUrl(region, idx, e.target.value)}
                      placeholder="https://example.com"
                      disabled={disabled}
                      className="flex-1 min-w-0 px-2.5 py-1.5 text-body-xs bg-white border border-border-subtle rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveUrl(region, idx)}
                      disabled={disabled || (urls || []).length <= 1}
                      className="px-2 py-1 text-body-xs font-medium text-text-tertiary hover:text-severity-high disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Remove URL">
                      &times;
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddUrl(region)}
                  disabled={disabled}
                  className="text-caption font-medium text-accent-purple hover:underline disabled:opacity-40 disabled:no-underline">
                  + Add URL
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlatformColumn({ title, websiteId, data, draft, onDraftChange, accentColor }) {
  if (!websiteId) {
    return (
      <div className="flex-1">
        <h3 className={`text-body-sm font-semibold ${accentColor} mb-4`}>{title}</h3>
        <div className="bg-surface-neutral rounded-xl p-6 text-center">
          <p className="text-body-xs text-text-tertiary">No {title.toLowerCase()} website linked</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <h3 className={`text-body-sm font-semibold ${accentColor} mb-4`}>{title}</h3>
      <div className="space-y-1 divide-y divide-border-subtle">
        {data && (
          <>
            <div className="pb-3">
              <label className={labelClass}>Name</label>
              <input
                type="text"
                value={draft.website_name ?? data.website_name ?? ""}
                onChange={(e) => onDraftChange({ ...draft, website_name: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="pb-3 pt-3">
              <label className={labelClass}>URL</label>
              <input
                type="text"
                value={draft.website_url ?? data.website_url ?? ""}
                onChange={(e) => onDraftChange({ ...draft, website_url: e.target.value })}
                className={inputClass}
              />
            </div>
          </>
        )}
        <SettingRow label="Subscription Plan" description="Controls feature access and limits">
          <select
            value={draft.subscription_plan ?? data?.subscription_plan ?? "free"}
            onChange={(e) => onDraftChange({ ...draft, subscription_plan: e.target.value })}
            className="px-3 py-2 text-body-xs bg-white border border-border-subtle rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 min-w-[130px]">
            {PLAN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </SettingRow>
        <div className="py-3">
          <p className="text-body-sm font-medium text-text-primary mb-1">HTTP Credentials</p>
          <p className="text-caption text-text-tertiary mb-2">Basic Auth for password-protected sites</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Username</label>
              <input
                type="text"
                value={draft.http_credentials?.username ?? data?.http_credentials?.username ?? ""}
                onChange={(e) => {
                  const current = draft.http_credentials ?? data?.http_credentials ?? {};
                  onDraftChange({ ...draft, http_credentials: { ...current, username: e.target.value } });
                }}
                className={inputClass}
                placeholder="Username"
              />
            </div>
            <div>
              <label className={labelClass}>Password</label>
              <input
                type="text"
                value={draft.http_credentials?.password ?? data?.http_credentials?.password ?? ""}
                onChange={(e) => {
                  const current = draft.http_credentials ?? data?.http_credentials ?? {};
                  onDraftChange({ ...draft, http_credentials: { ...current, password: e.target.value } });
                }}
                className={inputClass}
                placeholder="Password"
              />
            </div>
          </div>
        </div>
        <SettingRow label="Fixed IP" description="Route browser traffic via proxy">
          <Toggle
            enabled={draft.use_fixed_ip ?? data?.use_fixed_ip ?? false}
            onChange={(v) => onDraftChange({ ...draft, use_fixed_ip: v })}
          />
        </SettingRow>
        <SettingRow label="Web Bot Auth" description="Sign requests as bot.zenyt.ai (Shopify rate tiers)">
          <Toggle
            enabled={draft.use_web_bot_auth ?? data?.use_web_bot_auth ?? false}
            onChange={(v) => onDraftChange({ ...draft, use_web_bot_auth: v })}
          />
        </SettingRow>
        <SettingRow label="Beta Features" description="Enable experimental features">
          <Toggle
            enabled={draft.beta_features ?? data?.beta_features ?? false}
            onChange={(v) => onDraftChange({ ...draft, beta_features: v })}
          />
        </SettingRow>
        <SettingRow label="Scope Selection" description="Show scope selection UI to users">
          <Toggle
            enabled={draft.display_scope_selection ?? data?.display_scope_selection ?? false}
            onChange={(v) => onDraftChange({ ...draft, display_scope_selection: v })}
          />
        </SettingRow>
        <RegionUrlMappingEditor
          value={draft.region_url_mapping ?? data?.region_url_mapping}
          onChange={(v) => onDraftChange({ ...draft, region_url_mapping: v })}
        />
      </div>
    </div>
  );
}

export default function WebsiteParameters() {
  const selectedWebsiteId = useSelector(selectCurrentConsoleWebsiteId);
  const [consoleForm, setConsoleForm] = useState({ name: "", prod_website_id: "", staging_website_id: "" });
  const [stagingDraft, setStagingDraft] = useState({});
  const [prodDraft, setProdDraft] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [createConfirm, setCreateConfirm] = useState(null);

  const { data: consoleWebsite, isLoading, error } = useGetConsoleWebsiteByIdQuery(selectedWebsiteId, { skip: !selectedWebsiteId });
  const { data: stagingData } = useGetPlatformWebsiteByIdQuery({ websiteId: consoleWebsite?.staging_website_id, useProd: false }, { skip: !consoleWebsite?.staging_website_id });
  const { data: prodData } = useGetPlatformWebsiteByIdQuery({ websiteId: consoleWebsite?.prod_website_id, useProd: true }, { skip: !consoleWebsite?.prod_website_id });

  const [updateConsoleWebsite, { isLoading: isSavingConsole }] = useUpdateConsoleWebsiteMutation();
  const [updatePlatformWebsite, { isLoading: isSavingPlatform }] = useUpdateWebsiteMutation();
  const [createPlatformWebsite, { isLoading: isCreating }] = useCreateWebsiteMutation();
  const isSaving = isSavingConsole || isSavingPlatform;

  useEffect(() => {
    if (consoleWebsite) {
      setConsoleForm({
        name: consoleWebsite.name || "",
        prod_website_id: consoleWebsite.prod_website_id || "",
        staging_website_id: consoleWebsite.staging_website_id || "",
      });
      setStagingDraft({});
      setProdDraft({});
    }
  }, [consoleWebsite]);

  useEffect(() => {
    if (status.type === "success") {
      const t = setTimeout(() => setStatus({ type: "", message: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [status]);

  const handleSave = useCallback(async () => {
    if (!selectedWebsiteId) return;
    try {
      await updateConsoleWebsite({
        websiteId: selectedWebsiteId,
        website: {
          name: consoleForm.name,
          prod_website_id: consoleForm.prod_website_id,
          staging_website_id: consoleForm.staging_website_id,
        },
      }).unwrap();

      const platformUpdates = [];
      if (consoleForm.staging_website_id && Object.keys(stagingDraft).length > 0) {
        platformUpdates.push(
          updatePlatformWebsite({ websiteId: consoleForm.staging_website_id, website: stagingDraft, useProd: false }).unwrap()
        );
      }
      if (consoleForm.prod_website_id && Object.keys(prodDraft).length > 0) {
        platformUpdates.push(
          updatePlatformWebsite({ websiteId: consoleForm.prod_website_id, website: prodDraft, useProd: true }).unwrap()
        );
      }
      if (platformUpdates.length > 0) await Promise.all(platformUpdates);

      setStagingDraft({});
      setProdDraft({});
      setStatus({ type: "success", message: "Settings saved successfully" });
    } catch (err) {
      setStatus({ type: "error", message: err.data?.detail || "Failed to save settings" });
    }
  }, [selectedWebsiteId, consoleForm, stagingDraft, prodDraft, updateConsoleWebsite, updatePlatformWebsite]);

  const handleCreateCopy = useCallback(async (targetEnv) => {
    const sourceData = targetEnv === "staging" ? prodData : stagingData;
    if (!sourceData) return;
    try {
      const result = await createPlatformWebsite({
        website: {
          website_name: sourceData.website_name,
          website_url: sourceData.website_url,
        },
        useProd: targetEnv === "prod",
      }).unwrap();
      const newId = result.website_id;
      if (targetEnv === "staging") setConsoleForm((f) => ({ ...f, staging_website_id: newId }));
      else setConsoleForm((f) => ({ ...f, prod_website_id: newId }));
      setCreateConfirm(null);
      setStatus({ type: "success", message: `${targetEnv === "staging" ? "Staging" : "Production"} website created. Save to persist.` });
    } catch (err) {
      setStatus({ type: "error", message: err.data?.detail || `Failed to create ${targetEnv} website` });
      setCreateConfirm(null);
    }
  }, [prodData, stagingData, createPlatformWebsite]);

  const hasDirtyChanges = Object.keys(stagingDraft).length > 0 || Object.keys(prodDraft).length > 0 ||
    consoleForm.name !== (consoleWebsite?.name || "") ||
    consoleForm.prod_website_id !== (consoleWebsite?.prod_website_id || "") ||
    consoleForm.staging_website_id !== (consoleWebsite?.staging_website_id || "");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-neutral-subtle rounded w-48 animate-pulse" />
        <div className="bg-white rounded-2xl border border-border-subtle p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-surface-neutral rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-severity-high/5 rounded-2xl border border-severity-high/20 p-6">
        <p className="text-body-sm text-severity-high">{error.data?.detail || "Failed to load website data"}</p>
      </div>
    );
  }

  if (!selectedWebsiteId) {
    return (
      <div>
        <h1 className="text-h2 text-text-primary mb-2">Website Parameters</h1>
        <p className="text-body-sm text-text-tertiary">Select a website from the dropdown to configure settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2 text-text-primary">Website Parameters</h1>
          <p className="text-body-sm text-text-secondary mt-1">{consoleWebsite?.name || "Configure website settings"}</p>
        </div>
        <div className="flex items-center gap-3">
          {status.message && (
            <span className={`text-body-xs font-medium animate-fade-in ${status.type === "error" ? "text-severity-high" : "text-accent-green"}`}>
              {status.message}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasDirtyChanges}
            className="px-5 py-2 text-body-xs font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Console Setup */}
      <div className="bg-white rounded-2xl border border-border-subtle p-6">
        <h2 className="text-body-lg font-semibold text-text-primary mb-5">Console Setup</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass}>Console Website ID</label>
            <div className={readonlyClass}>{selectedWebsiteId}</div>
          </div>
          <div>
            <label className={labelClass}>Name</label>
            <input
              type="text"
              value={consoleForm.name}
              onChange={(e) => setConsoleForm((f) => ({ ...f, name: e.target.value }))}
              className={inputClass}
              disabled={isSaving}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-body-xs font-medium text-text-secondary">Staging Website ID</label>
              {!consoleForm.staging_website_id && consoleForm.prod_website_id && (
                <button type="button" onClick={() => setCreateConfirm("staging")} className="text-caption font-medium text-accent-purple hover:underline">
                  + Create from Prod
                </button>
              )}
            </div>
            <input
              type="text"
              value={consoleForm.staging_website_id}
              onChange={(e) => setConsoleForm((f) => ({ ...f, staging_website_id: e.target.value }))}
              className={inputClass}
              disabled={isSaving}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-body-xs font-medium text-text-secondary">Production Website ID</label>
              {!consoleForm.prod_website_id && consoleForm.staging_website_id && (
                <button type="button" onClick={() => setCreateConfirm("prod")} className="text-caption font-medium text-accent-green hover:underline">
                  + Create from Staging
                </button>
              )}
            </div>
            <input
              type="text"
              value={consoleForm.prod_website_id}
              onChange={(e) => setConsoleForm((f) => ({ ...f, prod_website_id: e.target.value }))}
              className={inputClass}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>

      {/* Platform Settings - Two Columns */}
      <div className="bg-white rounded-2xl border border-border-subtle p-6">
        <h2 className="text-body-lg font-semibold text-text-primary mb-5">Platform Settings</h2>
        <div className="flex gap-8">
          <PlatformColumn
            title="Staging"
            websiteId={consoleForm.staging_website_id}
            data={stagingData}
            draft={stagingDraft}
            onDraftChange={setStagingDraft}
            accentColor="text-accent-purple"
          />
          <div className="w-px bg-border-subtle flex-shrink-0" />
          <PlatformColumn
            title="Production"
            websiteId={consoleForm.prod_website_id}
            data={prodData}
            draft={prodDraft}
            onDraftChange={setProdDraft}
            accentColor="text-accent-green"
          />
        </div>
      </div>

      {createConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl border border-border-subtle">
            <h3 className="text-body-lg font-semibold text-text-primary mb-2">
              Create {createConfirm === "staging" ? "Staging" : "Production"} Website
            </h3>
            <p className="text-body-sm text-text-secondary mb-5">
              This will create a copy of the {createConfirm === "staging" ? "production" : "staging"} website
              (<strong>{createConfirm === "staging" ? prodData?.website_name : stagingData?.website_name}</strong>) on {createConfirm === "staging" ? "staging" : "production"}.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateConfirm(null)} className="px-4 py-2 text-body-xs font-medium text-text-secondary border border-border-default rounded-xl hover:bg-surface-neutral transition-colors">
                Cancel
              </button>
              <button onClick={() => handleCreateCopy(createConfirm)} disabled={isCreating}
                className="px-4 py-2 text-body-xs font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover disabled:opacity-40 transition-colors">
                {isCreating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
